import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Spinner } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import axios from 'axios';

export default function ImpersonatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken, setTenantInfo } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const tenantSubdomain = searchParams.get('tenant');
    
    if (!token) {
      setError('No token provided');
      return;
    }

    // Attempt to login with the provided token
    const doLogin = async () => {
      try {
        const result = await loginWithToken(token);
        if (result.success) {
          const user = result.user;
          const role = user?.role;
          
          // For instructor/learner, we need to get tenant info and redirect appropriately
          if ((role === 'instructor' || role === 'learner') && user?.tenant_id) {
            // Try to get tenant subdomain from URL param or fetch it
            let subdomain = tenantSubdomain;
            
            if (!subdomain) {
              // Fetch tenant info
              try {
                const tempApi = axios.create({
                  baseURL: API_URL,
                  headers: { Authorization: `Bearer ${token}` }
                });
                const tenantRes = await tempApi.get('/tenant/me');
                subdomain = tenantRes.data?.subdomain;
                
                // Store tenant info
                if (tenantRes.data) {
                  const info = {
                    id: tenantRes.data.id,
                    name: tenantRes.data.name,
                    subdomain: tenantRes.data.subdomain,
                    custom_domain: tenantRes.data.custom_domain
                  };
                  localStorage.setItem('lms_tenant_info', JSON.stringify(info));
                  localStorage.setItem('lms_tenant_subdomain', subdomain);
                  setTenantInfo(info);
                }
              } catch (err) {
                console.warn('Could not fetch tenant info:', err);
              }
            }
            
            // Store subdomain for logout redirect
            if (subdomain) {
              localStorage.setItem('lms_tenant_subdomain', subdomain);
            }
          }
          
          // Redirect based on user role
          if (role === 'super_admin') {
            navigate('/super-admin');
          } else if (role === 'tenant_admin') {
            navigate('/admin');
          } else if (role === 'instructor') {
            navigate('/instructor');
          } else if (role === 'learner') {
            navigate('/learn');
          } else {
            navigate('/');
          }
        } else {
          setError(result.error || 'Login failed');
        }
      } catch (err) {
        setError('Failed to authenticate with provided token');
      }
    };

    doLogin();
  }, [searchParams, loginWithToken, navigate, setTenantInfo]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-destructive">Authentication Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">{error}</p>
            <p className="mt-4 text-sm">
              <a href="/" className="text-primary hover:underline">Return to home</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Spinner className="w-12 h-12 animate-spin mx-auto text-primary" />
        <p className="mt-4 text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}
